import { sendEventToAnalytics } from 'core/crud/analytics';
import { useUser } from 'core/lite/hooks/useUser';
import { parseEducationYearName } from 'core/lite/parsers';
import * as coreEntities from 'core/store/index';
import { enrichOpenContentProperties } from 'core/utils/eventsAnalytics';
import { isLocalhost } from 'core/utils/utils';
import { EVENTS_ANALYTICS } from 'core/variables/events-analytics';
import { useSelector } from 'react-redux';
import { UAParser } from 'ua-parser-js';

const useEventsAnalytics = () => {
  const client = useSelector((state) => coreEntities.organization.selectors.getClient(state));
  const { user } = useUser();
  const ua = navigator.userAgent;
  const parser = new UAParser(ua);

  async function sendEventAnalytic(eventId, extraProperties, course = {}) {
    let userAgentProperties = getUserAgentProperties();
    let courseProperties = getCourseProperties(course);
    let userProperties = getUserProperties();

    let properties = {
      tenant: client,
      timestamp: new Date().getTime(),
      ...userProperties,
      ...courseProperties,
      ...extraProperties,
      ...userAgentProperties,
    };

    // TAN-9876: enrich the open_content event with content_id/content_name/
    // content_type/didactic_type so Santillana's Kinesis pipeline consistently
    // receives them. The enrichment itself is gated to OPEN_CONTENT only — it
    // is not applied to other events (see getUserAgentProperties below for a
    // change that IS shared by every event, not just OPEN_CONTENT).
    if (eventId === EVENTS_ANALYTICS.OPEN_CONTENT) {
      properties = enrichOpenContentProperties(properties);
    }

    if (!isLocalhost()) {
      await sendEventToAnalytics(eventId, properties);
    }
  }

  // Applies to every event sent through sendEventAnalytic, not just OPEN_CONTENT.
  function getUserAgentProperties() {
    return {
      browser_name: parser.getBrowser().name ?? null,
      browser_version: parser.getBrowser().version ?? null,
      os_name: parser.getOS().name ?? null,
      os_version: parser.getOS().version ?? null,
      // UAParser only classifies mobile/tablet/console/etc — it leaves `type`
      // undefined for desktop UAs rather than reporting 'desktop' explicitly, so
      // there's no reliable signal to distinguish an unrecognized UA (bot,
      // embedded webview) from an actual desktop browser. Report null in both
      // cases instead of guessing 'desktop'.
      device_type: parser.getDevice().type ?? null,
      device_vendor: parser.getDevice().vendor ?? null,
      device_model: parser.getDevice().model ?? null,
      cpu: parser.getCPU().architecture ?? null,
      language: navigator.language ?? null,
    };
  }

  function getCourseProperties(course) {
    return {
      discipline: course.discipline_name, //extra
      content_stage: course.education_level_name, //extra
      content_grade: parseEducationYearName(course.education_year_name), //extra
      course_guid: course.guid,
      education_level_guid: course.education_level_guid,
      education_year_guid: course.education_year_guid,
      discipline_guid: course.discipline_guid,
      collection_guid: course.collections && course.collections.length > 0 ? course.collections[0].guid : undefined,
      lang_guid: course.digital_program_langs && course.digital_program_langs.length > 0 ? course.digital_program_langs[0].id : undefined,
      country_guid: course.country_guid,
      template_guid: course.template_guid, //extra
    };
  }

  function getUserProperties() {
    return {
      user_guid: user.guid,
      role_guid: user.role_guid,
      state_guid: user.schools?.length > 0 ? user.schools[0].state_guid : null,
      city_guid: user.schools?.length > 0 ? user.schools[0].city_guid : null,
      user_school_guid: user.schools?.length > 0 ? user.schools[0].guid : null, //extra
    };
  }

  return { sendEventAnalytic };
};

export default useEventsAnalytics;
